import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbAlertTriangle, TbChevronRight, TbExternalLink, TbPlug } from "solid-icons/tb";
import { createMemo, createSignal, For, Index, Match, Show, Switch } from "solid-js";
import { tableClasses } from "../../block/markdownOptions";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { safeHttpsUrl } from "../utils/schemaUtils";
import { type MCPResultContent, type MCPResultPayload, parseMCPResultPayload } from "./mcpResultSchema";
import { isNumericCell, type McpTextPresentation, presentMcpResourceText, presentMcpText } from "./presentMcpText";

interface MCPResultRendererProps {
  block: StreamingBlock;
}

// A block cut at the converter's size cap arrives as a string that is no
// longer valid JSON; it is shown as-is rather than re-quoted.
const JsonBlock = (props: { value: unknown }) => (
  <pre class="max-h-64 overflow-auto rounded-md bg-inset p-2 font-mono text-xs wrap-break-word whitespace-pre-wrap text-primary-text">
    {typeof props.value === "string" ? props.value : JSON.stringify(props.value, null, 2)}
  </pre>
);

const MCPPresentation = (props: { presented: McpTextPresentation }) => {
  // Read once: the item is whole by the time the card renders.
  const presented = props.presented;
  return (
    <Switch>
      <Match when={presented.kind === "pre" && presented}>{(pre) => <JsonBlock value={pre().text} />}</Match>
      <Match when={presented.kind === "json" && presented}>
        {(json) => (
          <div>
            <span class="mb-1 inline-block rounded-full bg-primary-border/40 px-2 py-0.5 text-xs text-muted-text">
              JSON
            </span>
            <JsonBlock value={json().pretty} />
          </div>
        )}
      </Match>
      <Match when={presented.kind === "table" && presented}>
        {(table) => (
          <div class="max-h-64 overflow-auto">
            <table class={tableClasses.table}>
              <thead class={tableClasses.tableHead}>
                <tr class={tableClasses.tableRow}>
                  <For each={table().header}>
                    {(key) => <th class={`${tableClasses.tableCell} text-left`}>{key}</th>}
                  </For>
                </tr>
              </thead>
              <tbody class={tableClasses.tableBody}>
                <For each={table().rows}>
                  {(row) => (
                    <tr class={tableClasses.tableRow}>
                      <Index each={row}>
                        {(cell) => (
                          <td
                            class={tableClasses.tableCell}
                            classList={{ "text-right": isNumericCell(cell()), "text-left": !isNumericCell(cell()) }}
                          >
                            {cell() === null ? "" : String(cell())}
                          </td>
                        )}
                      </Index>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        )}
      </Match>
      <Match when={presented.kind === "markdown" && presented}>
        {(markdown) => (
          <div class="max-h-96 overflow-auto">
            <CompleteMarkdown markdown={markdown().markdown} />
          </div>
        )}
      </Match>
    </Switch>
  );
};

const MCPContentItem = (props: { item: MCPResultContent }) => {
  const rt = useChatRuntime();
  return (
    <Switch>
      <Match when={props.item.kind === "text" && props.item}>
        {(text) => (
          <div>
            <MCPPresentation presented={presentMcpText(text().text)} />
            <Show when={text().truncated}>
              <p class="mt-1 text-xs text-muted-text">{rt.t("mcp_result_card.truncated")}</p>
            </Show>
          </div>
        )}
      </Match>
      <Match when={props.item.kind === "image" && "placeholder" in props.item}>
        <p class="text-xs text-muted-text">{rt.t("mcp_result_card.image_omitted")}</p>
      </Match>
      <Match when={props.item.kind === "image" && "data" in props.item && props.item}>
        {(image) => (
          <img
            src={`data:${image().mimeType};base64,${image().data}`}
            alt={rt.t("mcp_result_card.image_alt")}
            class="max-h-64 max-w-full rounded-md"
          />
        )}
      </Match>
      <Match when={props.item.kind === "resource" && props.item}>
        {(resource) => (
          <div class="space-y-1">
            <Show when={resource().uri}>
              <Show
                when={safeHttpsUrl(resource().uri)}
                fallback={<p class="font-mono text-xs break-all text-muted-text">{resource().uri}</p>}
              >
                {(href) => (
                  <a
                    href={href()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex min-w-0 items-center gap-1 text-xs font-medium text-brand hover:underline"
                  >
                    <span class="truncate">{resource().uri}</span>
                    <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                  </a>
                )}
              </Show>
            </Show>
            <Show when={resource().text}>
              {(text) => <MCPPresentation presented={presentMcpResourceText(resource().mimeType, text())} />}
            </Show>
            <Show when={resource().truncated}>
              <p class="text-xs text-muted-text">{rt.t("mcp_result_card.truncated")}</p>
            </Show>
          </div>
        )}
      </Match>
      <Match when={props.item.kind === "json" && props.item}>{(json) => <JsonBlock value={json().json} />}</Match>
    </Switch>
  );
};

export const MCPResultRenderer = (props: MCPResultRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<MCPResultPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseMCPResultPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  // A failure opens on its own so the server's reason is readable without a click.
  const [expanded, setExpanded] = createSignal<boolean | null>(null);
  const isOpen = () => expanded() ?? payload()?.isError === true;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("mcp_result_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div
            class="overflow-hidden rounded-lg border bg-panel"
            classList={{ "border-primary-border": !data().isError, "border-red-500/50": data().isError }}
          >
            <button
              type="button"
              onClick={() => setExpanded(!isOpen())}
              aria-expanded={isOpen()}
              class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left hover:bg-panel-hover/40"
            >
              <Show when={safeHttpsUrl(data().server.iconUrl)} fallback={<TbPlug class="size-4 shrink-0 text-brand" />}>
                {(src) => <img src={src()} alt="" class="size-4 shrink-0 rounded-sm" />}
              </Show>
              <span class="truncate text-sm font-medium text-primary-text">{data().server.name}</span>
              <span class="truncate font-mono text-xs text-muted-text">{data().tool}</span>
              <span class="shrink-0 rounded-full bg-primary-border/40 px-2 py-0.5 text-xs text-muted-text">
                {data().server.slug}
              </span>
              <Show when={data().isError}>
                <span class="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-red-500">
                  <TbAlertTriangle class="size-3.5 shrink-0" />
                  {rt.t("mcp_result_card.failed")}
                </span>
              </Show>
              <TbChevronRight
                class="ml-auto size-3.5 shrink-0 text-muted-text transition-transform"
                classList={{ "rotate-90": isOpen() }}
                aria-hidden="true"
              />
            </button>

            <Show when={isOpen()}>
              <div class="space-y-3 border-t border-primary-border px-3 py-2">
                <Show when={Object.keys(data().args).length > 0}>
                  <div>
                    <p class="mb-1 text-xs font-medium text-muted-text">{rt.t("mcp_result_card.args_label")}</p>
                    <JsonBlock value={data().args} />
                  </div>
                </Show>
                <Show
                  when={data().content.length > 0}
                  fallback={<p class="text-xs text-muted-text">{rt.t("mcp_result_card.empty")}</p>}
                >
                  <div class="space-y-2" classList={{ "text-red-500": data().isError }}>
                    <For each={data().content}>{(item) => <MCPContentItem item={item} />}</For>
                  </div>
                </Show>
              </div>
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
